import React, { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import CookieSettings, { type CookiePreferences } from "./CookieSettings";

interface CookieDisclaimerProps {
  isVisible: boolean;
  onAccept: (preferences: CookiePreferences) => void;
  onClose: () => void;
}

const CookieDisclaimer = ({ isVisible, onAccept, onClose }: CookieDisclaimerProps) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);

  const handleAcceptAll = () => {
    onAccept({ necessary: true, analytics: true, marketing: true, preferences: true });
  };

  const handleReject = () => {
    onAccept({ necessary: true, analytics: false, marketing: false, preferences: false });
  };

  const isAr = language === 'ar';

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="w-full max-w-lg"
          >
            <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-2xl shadow-black/20">
              {/* Top gradient accent bar */}
              <div className="h-1 w-full bg-gradient-to-r from-primary via-accent to-primary" />

              <div className="p-3 sm:p-5">
                <div className="flex items-start gap-3 sm:gap-4">
                  {/* Cookie icon blob */}
                  <div className="flex-shrink-0 h-9 w-9 sm:h-11 sm:w-11 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/20 flex items-center justify-center">
                    <Cookie className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-base text-foreground leading-tight mb-1">
                      {isAr ? 'نستخدم ملفات تعريف الارتباط' : 'We use cookies'}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                      {isAr
                        ? 'لتحسين تجربتك وتوفير محتوى مخصص. يمكنك تخصيص تفضيلاتك في أي وقت.'
                        : 'To enhance your experience and provide personalized content. Customize your preferences anytime.'
                      }
                    </p>

                    {/* Buttons */}
                    <div className="flex items-center gap-2 flex-col sm:flex-row flex-wrap">
                      <Button
                        onClick={handleAcceptAll}
                        size="sm"
                        className="h-9 px-4 text-xs font-semibold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground shadow-sm w-full sm:w-auto"
                      >
                        {isAr ? 'قبول الكل' : 'Accept All'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSettings(true)}
                        className="h-9 px-4 text-xs font-medium border-border/60 hover:bg-accent/50 w-full sm:w-auto"
                      >
                        {isAr ? 'تخصيص' : 'Customize'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleReject}
                        className="h-9 px-3 text-xs text-muted-foreground hover:text-foreground hover:bg-accent/40 w-full sm:w-auto"
                      >
                        {isAr ? 'رفض' : 'Reject'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/privacy-policy')}
                        className="h-9 px-3 text-xs text-muted-foreground hover:text-foreground hover:bg-accent/40 w-full sm:w-auto sm:ml-auto"
                      >
                        {isAr ? 'الخصوصية' : 'Privacy Policy'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <CookieSettings
              isVisible={showSettings}
              onClose={() => setShowSettings(false)}
              onSave={(prefs) => { onAccept(prefs); setShowSettings(false); }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieDisclaimer;
